/**
 * Type guards and helper functions for safely working with data
 */

import { Product, BillingPeriod, ProductStatus } from '@/types/product';
import { Subscription } from '@/types/subscription';
import { getBillingPeriod } from '@/utils/billingPeriodUtils';

/**
 * Type guard to check if an object has a specific property
 */
export function hasProperty<T extends object, K extends PropertyKey>(
  obj: T,
  prop: K
): obj is T & Record<K, unknown> {
  return Object.prototype.hasOwnProperty.call(obj, prop);
}

/**
 * Gets a string property from an object with a fallback value
 */
export function getStringProperty<T>(obj: T, key: keyof T, fallback: string = ''): string {
  if (!obj) return fallback;
  const value = obj[key];
  return typeof value === 'string' ? value : fallback;
}

/**
 * Gets a number property from an object with a fallback value
 */
export function getNumberProperty<T>(obj: T, key: keyof T, fallback: number = 0): number {
  if (!obj) return fallback;
  const value = obj[key];
  return typeof value === 'number' ? value : fallback;
}

/**
 * Gets a boolean property from an object with a fallback value
 */
export function getBooleanProperty<T>(obj: T, key: keyof T, fallback: boolean = false): boolean {
  if (!obj) return fallback;
  const value = obj[key];
  return typeof value === 'boolean' ? value : fallback;
}

/**
 * Safe accessor for product fields with appropriate defaults
 */
export function safeProductAccess(product: Partial<Product> | null | undefined): Product {
  if (!product) {
    return {
      id: '',
      title: '',
      amount: 0,
      status: 'archived', // Changed from 'inactive' to 'archived'
      created_at: new Date().toISOString(),
      billing_period: 'one_time',
      user_id: ''
    };
  }
  
  // Safely handle status with type validation
  const rawStatus = getStringProperty(product, 'status', 'archived'); // Changed default from 'inactive' to 'archived'
  // Ensure status is one of the allowed values
  const status = (rawStatus === 'active' || rawStatus === 'archived') 
    ? rawStatus 
    : 'archived'; // Default to 'archived' instead of 'inactive'
  
  return {
    id: getStringProperty(product, 'id', ''),
    title: getStringProperty(product, 'title', getStringProperty(product, 'name', '')),
    name: getStringProperty(product, 'name', getStringProperty(product, 'title', '')),
    description: getStringProperty(product, 'description', ''),
    amount: getNumberProperty(product, 'amount', getNumberProperty(product, 'price_cents', 0)),
    price_cents: getNumberProperty(product, 'price_cents', getNumberProperty(product, 'amount', 0)),
    status: status as ProductStatus,
    created_at: getStringProperty(product, 'created_at', new Date().toISOString()),
    billing_period: getBillingPeriod(getStringProperty(product, 'billing_period', 'one_time')),
    use_anchor_billing: getBooleanProperty(product, 'use_anchor_billing', false),
    anchor_billing_day: getNumberProperty(product, 'anchor_billing_day', 0),
    user_id: getStringProperty(product, 'user_id', '')
  };
}

/**
 * Safe accessor for subscription fields with appropriate defaults
 */
export function safeSubscriptionAccess(subscription: Partial<Subscription> | null | undefined): Subscription {
  if (!subscription) {
    return {
      id: '',
      name: '',
      title: '',
      amount: 0,
      interval: '',
      recurringPeriod: '',
      currency: 'USD',
      created_at: new Date().toISOString(),
      status: 'archived', // Changed from 'inactive' to 'archived'
      isSubscription: true,
      user_id: ''
    };
  }
  
  // Safely handle status with type validation
  const rawStatus = getStringProperty(subscription, 'status', 'active');
  // Ensure status is one of the allowed values
  const status = (rawStatus === 'active' || rawStatus === 'archived') 
    ? rawStatus 
    : 'archived'; // Default to 'archived' instead of 'inactive'
  
  return {
    id: getStringProperty(subscription, 'id', ''),
    name: getStringProperty(subscription, 'name', getStringProperty(subscription, 'title', '')),
    title: getStringProperty(subscription, 'title', getStringProperty(subscription, 'name', '')),
    description: getStringProperty(subscription, 'description', ''),
    amount: getNumberProperty(subscription, 'amount', 0),
    interval: getStringProperty(subscription, 'interval', getStringProperty(subscription, 'recurringPeriod', '')),
    recurringPeriod: getStringProperty(subscription, 'recurringPeriod', getStringProperty(subscription, 'interval', '')),
    currency: getStringProperty(subscription, 'currency', 'USD'),
    created_at: getStringProperty(subscription, 'created_at', new Date().toISOString()),
    status: status as 'active' | 'archived',
    useAnchorBilling: getBooleanProperty(subscription, 'useAnchorBilling', false),
    anchorBillingDay: getNumberProperty(subscription, 'anchorBillingDay', 0),
    isSubscription: true,
    user_id: getStringProperty(subscription, 'user_id', '')
  };
}
